import Accordion from "@mui/material/Accordion";
import AccordionActions from "@mui/material/AccordionActions";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { GroupMemberInfo } from "../types/gigya";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

type AccordianTableItemProps = {
  row: GroupMemberInfo;
  onPropStatusChange: (row: GroupMemberInfo, status: string) => void;
  onPropRoleChange: (row: GroupMemberInfo, role: string) => void;
  remove: () => void;
  removeDisabled: boolean;
};
export const AccordionTableItem = (props: AccordianTableItemProps) => {
  const { row, onPropStatusChange, onPropRoleChange, remove, removeDisabled } =
    props;
  const { t } = useTranslation();

  const onStatusChange = (event: SelectChangeEvent, row: GroupMemberInfo) => {
    onPropStatusChange(row, event?.target.value);
  };

  const onRoleChange = (event: SelectChangeEvent, row: GroupMemberInfo) => {
    onPropRoleChange(row, event?.target.value);
  };

  return (
    <div>
      <Accordion
        sx={{
          mb: 0.5,
          boxShadow: "none",
          "&:before": { display: "none" },
          borderBottom: "0.5px solid #ddd",
          overflow: "hidden", // Ensure no overflow beyond boundaries
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
          sx={{
            bgcolor: "#F1F6FB",
            "& .MuiAccordionSummary-content": {
              margin: 0,
            },
            "& .MuiAccordionSummary-expandIconWrapper": {
              transform: "rotate(0deg)", // Align icon with summary to prevent overflow
            },
            padding: "5px 10px",
          }}
        >
          <Typography
            component="span"
            sx={{
              fontWeight: "bold",
              fontSize: "1rem",
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
            }}
          >{`${row.profile.lastName}, ${row.profile.firstName}`}</Typography>
        </AccordionSummary>
        {process.env.REACT_APP_LOCALE !== "en-us" && (
          <AccordionDetails sx={{ bgcolor: "#D6E3F0", padding: "5px 10px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              <Typography
                sx={{
                  fontWeight: "bold",
                  flexShrink: 0,
                  marginRight: "8px",
                  minWidth: "58px",
                }}
              >
                {`${t("exported.Status")}:`}
              </Typography>
              <Select
                sx={{
                  boxShadow: "none",
                  ".MuiOutlinedInput-notchedOutline": { border: 0 },
                  ".MuiSelect-select": { marginBottom: "20px" },
                  minWidth: 120,
                }}
                id="member-status-id"
                value={row.relationshipData.status}
                label={row.relationshipData.status}
                onChange={(event) => onStatusChange(event, row)}
              >
                <MenuItem value="approved">{t("approve")}</MenuItem>
                <MenuItem value="rejected">{t("reject")}</MenuItem>
                <MenuItem
                  value="pending"
                  disabled={row.relationshipData.status === "rejected"}
                >
                  {t("pendingRequest")}
                </MenuItem>
              </Select>
            </div>
          </AccordionDetails>
        )}
        <AccordionDetails sx={{ bgcolor: "#D6E3F0", padding: "5px 10px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{
                fontWeight: "bold",
                flexShrink: 0,
                marginRight: "8px",
                minWidth: "58px",
              }}
            >
              {`${t("exported.Role")}:`}
            </Typography>
            <Select
              sx={{
                boxShadow: "none",
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                ".MuiSelect-select": { marginBottom: "20px" },
                minWidth: 120,
              }}
              id="member-role-id"
              value={row.relationshipData.role}
              label={row.relationshipData.role}
              onChange={(event) => onRoleChange(event, row)}
            >
              <MenuItem value="admin">{t("exported.Admin")}</MenuItem>
              {process.env.REACT_APP_LOCALE !== "en-us" && (
                <MenuItem value="employee">{t("exported.Employee")}</MenuItem>
              )}
              <MenuItem value="member">{t("exported.Member")}</MenuItem>
            </Select>
          </div>
        </AccordionDetails>
        <AccordionDetails sx={{ bgcolor: "#D6E3F0", padding: "5px 10px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              overflowWrap: "anywhere",
            }}
          >
            <Typography
              sx={{
                fontWeight: "bold",
                flexShrink: 0,
                marginRight: "8px",
                minWidth: "58px",
              }}
            >
              {`${t("exported.Email")}:`}
            </Typography>
            <div style={{ paddingLeft: "14px" }}>{row.profile.email}</div>
          </div>
        </AccordionDetails>
        <AccordionActions sx={{ bgcolor: "#D6E3F0", padding: "5px 10px" }}>
          <IconButton
            aria-label="delete"
            onClick={remove}
            disabled={removeDisabled}
          >
            <DeleteIcon />
          </IconButton>
        </AccordionActions>
      </Accordion>
    </div>
  );
};
